import React, { useState, useEffect, useMemo } from "react";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import SelectOption from "components/common/Select/Select";
// import { NumericFormat } from "react-number-format";
// import SwitchWithIcon from "components/common/SwitchWithIcon";

const MAX_NAME_LENGTH = 150;
const EMPTY_CATEGORIES = [];

const getDocumentKey = (document) =>
  document?.uuid ?? (document?.id != null ? String(document.id) : "");

const EditDocumentModal = ({
  isOpen,
  onClose,
  document: doc,
  categories = EMPTY_CATEGORIES,
  onSave,
  isLoading = false,
  categoryIdField = "worker_compliance_category_id",
  hideCategoryField = false,
}) => {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [score, setScore] = useState(0);
  const [isCompliance, setIsCompliance] = useState(false);

  const visibleCategories = useMemo(
    () => (categories || []).filter((c) => c.is_visible !== false),
    [categories]
  );

  const categoryOptions = useMemo(
    () =>
      visibleCategories.map((c) => ({
        value: c.id != null ? String(c.id) : "",
        label: c.name,
      })),
    [visibleCategories]
  );

  const selectedCategoryOption =
    categoryId != null && categoryId !== ""
      ? categoryOptions.find((opt) => opt.value === String(categoryId)) ?? null
      : null;

  const documentKey = getDocumentKey(doc);

  useEffect(() => {
    if (!isOpen || !doc) return;
    setName(doc.name ?? "");
    setNameError("");
    setCategoryId(
      doc[categoryIdField] != null
        ? String(doc[categoryIdField])
        : visibleCategories[0]?.id != null
          ? String(visibleCategories[0].id)
          : ""
    );
    setScore(Number(doc.score) ?? 0);
    setIsCompliance(!!doc.is_compliance);
    // Reset only when the modal opens or a different document is selected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, documentKey, categoryIdField]);

  const handleSave = () => {
    if (!doc || isLoading) return;
    setNameError("");
    const trimmedName = (name ?? "").trim();
    if (!trimmedName) {
      setNameError("Document name is required.");
      return;
    }
    if (name.length > MAX_NAME_LENGTH) {
      setNameError("Document name must not exceed 150 characters.");
      return;
    }
    const scoreNum = Math.min(100, Math.max(0, Number(score)));
    const catId = hideCategoryField
      ? null
      : categoryId != null && categoryId !== ""
        ? Number(categoryId)
        : visibleCategories.length > 0
          ? visibleCategories[0]?.id ?? null
          : doc[categoryIdField] ?? null;
    onSave(trimmedName, scoreNum, isCompliance, catId);
  };

  if (!doc) return null;

  return (
    <ModalSkeleton
      isOpen={isOpen}
      title="Edit document"
      closeModal={isLoading ? () => {} : onClose}
      cssClass="w-[90%] max-w-md z-[99999]"
      modalFooter={
        <div className="flex justify-end gap-2">
          <CustomButton
            type="button"
            label="Cancel"
            variant="secondary"
            clickHandler={onClose}
            isDisabled={isLoading}
          />
          <CustomButton
            type="button"
            label="Save"
            variant="primary"
            clickHandler={handleSave}
            showLoading={isLoading}
            isDisabled={
              isLoading ||
              !(name ?? "").trim() ||
              name.length > MAX_NAME_LENGTH ||
              (!hideCategoryField &&
                visibleCategories.length > 0 &&
                (categoryId === "" || categoryId == null))
            }
          />
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Document name <span className="text-red-500">*</span> (max {MAX_NAME_LENGTH} characters)
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              const val = e.target.value;
              setName(val);
              if (nameError && val.length <= MAX_NAME_LENGTH) setNameError("");
            }}
            disabled={isLoading}
            className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-50 disabled:cursor-not-allowed ${
              nameError ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="e.g. Police check"
            maxLength={MAX_NAME_LENGTH}
          />
          {nameError && (
            <p className="mt-1 text-sm text-red-600" role="alert">
              {nameError}
            </p>
          )}
          <p className="mt-0.5 text-xs text-gray-500">
            {name.length}/{MAX_NAME_LENGTH}
          </p>
        </div>
        {visibleCategories.length > 0 && !hideCategoryField && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <SelectOption
              data={categoryOptions}
              selectedOption={selectedCategoryOption}
              handleChange={(option) =>
                setCategoryId(
                  option?.value != null && option.value !== ""
                    ? String(option.value)
                    : ""
                )
              }
              placeHolder="Select category"
              isClearable
              cssClass="w-full"
            />
          </div>
        )}
        {/* Mandatory and Weight (%) – planned for a future release */}
        {/* <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mandatory</label>
          <SwitchWithIcon
            checked={isCompliance}
            onChange={setIsCompliance}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Percentage (%)</label>
          <NumericFormat
            value={score}
            onValueChange={(v) => setScore(v.floatValue ?? 0)}
            allowNegative={false}
            min={0}
            max={100}
            suffix="%"
            decimalScale={0}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div> */}
      </div>
    </ModalSkeleton>
  );
};

export default EditDocumentModal;
